// The live recheck route (bead hf-e6-commitments-coverage-udt.5).
//
// Subscription and mutation plumbing only. Every decision about words and layout
// belongs to `RecheckSurfaces.tsx`, which is why that file is testable without a
// server and this one is thin enough to read in one sitting.
//
// ── THE REVISION GOES BACK WITH THE ANSWER ─────────────────────────────────────
//
// `needRevision` is not bookkeeping. It is the revision this person was actually
// SHOWN, and sending it back is what lets the server refuse a Yes that arrived after
// a second edit. Without it a fast follow-up edit would harvest the consent somebody
// gave to the first version — silent re-consent, which invariant 4 forbids and which
// this entire flow exists to make impossible. So it travels from the query, through
// the card, back to the mutation, untouched.
//
// ── REFUSALS ARE ANSWERS, NOT ERRORS ───────────────────────────────────────────
//
// "The plan changed again while you were reading" and "somebody else took the last
// place" are both legitimate outcomes of pressing Yes, and each is a different
// sentence a person needs to read. They are rendered from `claim`'s existing
// rejection mapping rather than a second one written here — one refusal vocabulary,
// shared with volunteering.

import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import { commitmentMutations, commitmentQueries } from "../../convex/commitments";
import { needQueries } from "../../convex/needs";
import { messageFor } from "../claim";
import {
  RecheckAudiencePanel,
  RecheckList,
  type ProxyAnswerChoice,
  type RecheckState,
} from "./RecheckSurfaces";
import { toRecheckViews, type RecheckView } from "./recheckData";

export interface RecheckRouteProps {
  readonly circleId: string;
  /** The circle's IANA zone, from the plan read. Windows are shown in ITS hours. */
  readonly timeZone: string;
  /** Builds need routes. An opaque identifier, never anything about a person. */
  readonly publicPlanRef: string;
  /**
   * Render nothing at all when there is nothing to answer.
   *
   * For placements that are somebody ELSE'S screen — Today, whose job is the day. An
   * "everything is fine" panel there would be a permanent reminder that things
   * sometimes are not, on a screen a family opens every morning. On the recheck
   * screen itself the empty state is the whole answer, so it stays.
   */
  readonly hideWhenEmpty?: boolean;
  /**
   * Passed straight through to the cards (bead hf-e6-commitments-coverage-udt.7).
   *
   * The offline bead owns the connectivity store; this route only forwards its
   * verdict, so neither feature has to import the other's internals.
   */
  readonly actionsPaused?: boolean;
}

/**
 * A helper's outstanding rechecks, answered one at a time.
 *
 * The list is reactive: an organiser who reverts their edit removes the question
 * from this screen without the person doing anything, and a Yes removes its own card
 * because the commitment is no longer `reconfirmation_required`.
 */
export function RecheckRoute({
  circleId,
  timeZone,
  publicPlanRef,
  hideWhenEmpty = false,
  actionsPaused = false,
}: RecheckRouteProps) {
  const rechecks = useQuery(commitmentQueries.myRechecks, { circleId });
  const reconfirm = useMutation(commitmentMutations.reconfirm);
  const [state, setState] = useState<RecheckState>({ status: "idle" });

  if (rechecks === undefined) return null;
  if (hideWhenEmpty && rechecks.length === 0) return null;

  const answer = (recheck: RecheckView, decision: "yes_still_can" | "no_cannot") => {
    // The double-tap guard. One question, one answer in flight: a second tap while
    // the first is travelling would spend the idempotency key on a race with itself.
    if (state.status === "sending") return;
    setState({ status: "sending", commitmentId: recheck.commitmentId });

    void reconfirm({
      circleId,
      commitmentId: recheck.commitmentId,
      // The revision they were SHOWN, not the newest one. See the header.
      needRevision: recheck.needRevision,
      decision,
      idempotencyKey: `recheck-${recheck.commitmentId}-${String(recheck.needRevision)}-${decision}`,
    })
      .then((outcome) => {
        // `replayed` is the SAME answer arriving twice — a retry of a request that
        // already succeeded. Treating it as anything but success would show somebody
        // a refusal for a decision the server has already recorded.
        if (outcome.status === "replayed" || outcome.value.ok) {
          // The card disappears on its own: the query re-runs because the commitment
          // is no longer awaiting an answer. Nothing here removes it by hand.
          setState({ status: "idle" });
          return;
        }
        setState({
          status: "refused",
          commitmentId: recheck.commitmentId,
          message: messageFor(outcome.value.rejection).body,
        });
      })
      .catch(() => {
        // Fail-closed and VISIBLE (PLT-011): an offline answer is not queued and not
        // pretended. The person is told, and their commitment is untouched — which is
        // the honest outcome, since nothing reached the server.
        setState({
          status: "refused",
          commitmentId: recheck.commitmentId,
          message: messageFor({ kind: "not_found", entity: "commitment" }).body,
        });
      });
  };

  return (
    <RecheckList
      actionsPaused={actionsPaused}
      onAnswer={answer}
      rechecks={toRecheckViews(rechecks, { timeZone, publicPlanRef })}
      state={state}
    />
  );
}

export interface RecheckAudienceRouteProps {
  readonly circleId: string;
  readonly supportNeedId: string;
  /**
   * Server-decided: may this person record what somebody else said?
   * (`commitment.record.proxy`; bead hf-ww3.) Never inferred here (AUT-007).
   */
  readonly canRecordProxyAnswers?: boolean;
  readonly actionsPaused?: boolean;
}

/**
 * Who the organiser is waiting on, beside the need they changed (plan §10.9).
 *
 * Renders NOTHING when nobody has ever committed to this need. A panel reading
 * "nobody had agreed to this yet" on every freshly created need would be noise on the
 * screen an organiser uses most — the sentence exists for the case where somebody
 * MADE a change and wants to know who it reached, and this route only reaches that
 * case by having something to say.
 */
export function RecheckAudienceRoute({
  circleId,
  supportNeedId,
  canRecordProxyAnswers = false,
  actionsPaused = false,
}: RecheckAudienceRouteProps) {
  const result = useQuery(commitmentQueries.recheckAudience, { circleId, supportNeedId });
  const reconfirmProxy = useMutation(commitmentMutations.reconfirmProxy);
  const need = useQuery(needQueries.get, { circleId, supportNeedId });

  if (!result?.ok) return null;

  const { awaiting, reconfirmed, declined } = result.value;
  if (awaiting.length + reconfirmed.length + declined.length === 0) return null;

  const record = (choice: ProxyAnswerChoice) => {
    if (actionsPaused) return;
    // The revision the organiser was LOOKING AT when they recorded the answer. If the
    // need moved again while the dialog was open the server refuses, rather than
    // binding somebody's re-agreement to a version nobody has described to them.
    const needRevision = need?.ok === true ? need.value.revision : null;
    if (needRevision === null) return;

    void reconfirmProxy({
      circleId,
      commitmentId: choice.commitmentId,
      needRevision,
      decision: choice.decision,
      agreementSource: choice.agreementSource,
      consentConfirmed: true,
      idempotencyKey: `proxy-recheck-${choice.commitmentId}-${String(needRevision)}-${choice.decision}`,
    }).catch(() => {
      // Fail-closed and silent HERE, deliberately: nothing was optimistically moved,
      // so the person stays in "Still to answer" and the organiser can see the answer
      // did not land. A toast that said "recorded" would be the one lie this surface
      // must never tell.
    });
  };

  return (
    <RecheckAudiencePanel
      actionsPaused={actionsPaused}
      audience={{ awaiting, reconfirmed, declined }}
      canRecordProxyAnswers={canRecordProxyAnswers}
      onProxyAnswer={record}
    />
  );
}
